import { BehaviorSubject, Subject, map, Observable, shareReplay, switchMap, take, tap, merge, catchError, of } from 'rxjs';

import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { BaseModel } from '../models/base.model';

import { ResourceAbstractService } from './resource-abstract.service';

// F => Frontend model
// B => Backend api model
// L => List item model
// BL => Backend List item api model
export abstract class AllResourceAbstractService<F extends BaseModel<F, B, IdType>, B, L, BL, IdType = number> extends ResourceAbstractService<F, B, IdType> {
    protected refreshAllSubject = new BehaviorSubject<void>(undefined);

    protected refreshAllManualSubject = new Subject<L[]>();

    // TODO: kind of hacky, we should maybe clean this up
    // but we have refreshAllManualSubject, because in some instances we need to wait for the get all to finish returning, so in that scenario
    // we can call refreshAllManual(), instead of refreshAllSubject.next(), to update the all$
    all$: Observable<L[]> = merge(
        this.refreshAllSubject.asObservable().pipe(
            switchMap(() => this.refreshAllManual())
        ),
        this.refreshAllManualSubject
    ).pipe(
        shareReplay(1)
    );

    abstract override getApiService(): MultiBaseAbstractApi<B, BL, IdType>;
    abstract constructListModel(listApiModel: BL): L;

    isNameDuplicated$(name: string, id?: IdType): Observable<boolean> {
        return this.all$.pipe(
            map(list => {
                if (!list || !name) {
                    return false;
                }
                const isDuplicated = list.find(listItem => {
                    if (id) {
                        return (listItem as AuditableModel<IdType>).name.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase()
                        && (listItem as AuditableModel<IdType>).id !== id;
                    }
                    return (listItem as AuditableModel<IdType>).name.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase();
                });

                return !!isDuplicated;
            }),
            take(1)
        );
    }

    refreshAllManual(): Observable<L[]> {
        return this.getApiService().getList().pipe(
            map(list => list.map(item => this.constructListModel(item))),
            catchError((error) => {
                if (error.status === 404) {
                    return of([]);
                }
                throw error;
            }),
            tap((list) => this.refreshAllManualSubject.next(list)),
        );
    }

    refreshAll() {
        this.refreshAllSubject.next();
    }
}
